import { useState } from 'react';
import { HiX, HiOutlineShieldCheck } from 'react-icons/hi';
import { useAuth } from '../hooks/useAuth';

export default function MFAVerifyOverlay() {
  const {
    verify2FA, isVerifying2FA,
    challengeMFA, isChallengingMFA,
    logout, mfaTempToken, pendingTokens, user,
  } = useAuth();
  const [code, setCode] = useState('');
  const [infoDismissed, setInfoDismissed] = useState(false);

  const isPending = isVerifying2FA || isChallengingMFA;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    if (mfaTempToken) {
      verify2FA({ temp_token: mfaTempToken, otp_code: code });
    } else if (pendingTokens?.access) {
      challengeMFA({ otpCode: code, accessToken: pendingTokens.access });
    }
  };

  const roleLabel = user?.role?.replace(/_/g, ' ') || '';

  return (
    <div className="flex h-full items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-md overflow-hidden">

        {/* Title bar */}
        <div className="px-8 pt-8 pb-4">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-600 font-bold text-white text-sm flex-shrink-0">
              M
            </div>
            <h1 className="text-xl font-bold text-secondary-900">
              Verify MFA for MMPR
            </h1>
          </div>

          {/* Info banner */}
          {!infoDismissed && (
            <div className="flex items-center justify-between rounded-lg border border-primary-300 bg-primary-50 px-4 py-3 mb-5">
              <p className="text-sm text-primary-800 font-medium">
                Welcome! Please enter your one-time code.
              </p>
              <button
                type="button"
                onClick={() => setInfoDismissed(true)}
                className="ml-3 flex-shrink-0 text-primary-400 hover:text-primary-600 transition-colors"
              >
                <HiX className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Signed-in-as */}
          <div className="flex items-center gap-2 text-sm text-secondary-600 mb-5">
            <span>You are signed in as</span>
            <span className="font-semibold text-secondary-900">{user?.email}</span>
            {roleLabel && (
              <span className="rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white capitalize">
                {roleLabel}
              </span>
            )}
          </div>

          <div className="border-t border-secondary-200" />
        </div>

        {/* OTP Form */}
        <form onSubmit={handleSubmit} className="px-8 pb-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              <span className="text-danger-500 mr-0.5">*</span>
              Enter your One-Time Code
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="012345"
              autoFocus
              className="block w-full rounded-lg border border-secondary-300 bg-white px-4 py-3 text-lg font-mono tracking-[0.4em] text-secondary-900 placeholder:text-secondary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </div>

          <button
            type="submit"
            disabled={code.length !== 6 || isPending}
            className="btn-primary w-full py-2.5"
          >
            {isPending ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Verifying...
              </>
            ) : (
              <>
                <HiOutlineShieldCheck className="h-4 w-4" />
                Verify
              </>
            )}
          </button>

          <div className="border-t border-secondary-200 pt-4">
            <p className="text-sm text-secondary-500 text-center">
              Not you?{' '}
              <button
                type="button"
                onClick={() => logout()}
                className="text-primary-600 hover:underline font-medium"
              >
                Sign in with a different account
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
